import React, { useState } from 'react';
import { useApp } from '../hooks/AppContext';
import { api } from '../utils/api';
import ErrorModal from './ErrorModal';

// DD-MM-YYYY_Complete_Data_backup.json
function backupFilename() {
  const d = new Date();
  const p = (n) => String(n).padStart(2, '0');
  return `${p(d.getDate())}-${p(d.getMonth() + 1)}-${d.getFullYear()}_Complete_Data_backup.json`;
}

const TABLE_LABEL = {
  users: 'User accounts',
  branches: 'Branch master',
  acodes: 'Acode master',
  banks: 'Bank master',
  vendors: 'Vendor master',
  godowns: 'Godown records',
  overrides: 'Month overrides',
  confirmations: 'Confirmations (all months)',
  cutoffs: 'Cut-offs',
};

export default function BackupPage() {
  const { isAdmin } = useApp();
  const [busy, setBusy] = useState('');
  const [errModal, setErrModal] = useState(null);
  const [notice, setNotice] = useState(null);

  // Restore state
  const [file, setFile] = useState(null);
  const [parsed, setParsed] = useState(null);
  const [password, setPassword] = useState('');
  const [typed, setTyped] = useState('');

  const fail = (messages, title) =>
    setErrModal({ messages: Array.isArray(messages) ? messages : [messages], title });

  const handleExport = async () => {
    setBusy('export');
    try {
      const data = await api.exportBackup();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = backupFilename();
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      const total = Object.values(data.meta?.counts || {}).reduce((s, n) => s + n, 0);
      setNotice(`Saved ${backupFilename()} — ${total} rows across ${Object.keys(data.tables).length} tables.`);
    } catch (err) {
      fail(err.message, 'Export failed');
    }
    setBusy('');
  };

  // Parsed in the browser first, so the row counts can be checked before
  // anything is sent to the server.
  const handleFile = (e) => {
    const f = e.target.files[0];
    setFile(null); setParsed(null); setTyped(''); setPassword('');
    if (!f) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const json = JSON.parse(ev.target.result);
        if (!json?.tables || typeof json.tables !== 'object') {
          fail('That file has no "tables" section — it is not a Complete Data backup.', 'Unusable file');
          return;
        }
        setFile(f);
        setParsed(json);
      } catch {
        fail('That file is not valid JSON.', 'Unusable file');
      }
    };
    reader.readAsText(f);
    e.target.value = '';
  };

  const handleRestore = async () => {
    if (typed !== 'RESTORE') { fail('Type RESTORE in capitals to confirm.', 'Not confirmed'); return; }
    if (!password) { fail('Enter your admin password.', 'Not confirmed'); return; }
    setBusy('restore');
    try {
      const res = await api.restoreBackup(password, parsed);
      const lines = Object.entries(res.restored).map(([t, n]) => `${TABLE_LABEL[t] || t}: ${n}`);
      setNotice(`Restore complete — ${lines.join(' · ')}.` +
        (res.selfSurvives ? ' Log out and back in to refresh your session.'
                          : ' Your own account was not in this backup — log out now and sign in with an account that is.'));
      setFile(null); setParsed(null); setPassword(''); setTyped('');
    } catch (err) {
      fail(err.message, 'Restore failed — nothing was changed');
    }
    setBusy('');
  };

  const counts = parsed?.tables
    ? Object.entries(parsed.tables).map(([t, rows]) => [t, Array.isArray(rows) ? rows.length : 0])
    : [];

  return (
    <div className="wrap">
      <div className="page-header">
        <div>
          <h2>💾 Backup &amp; Restore</h2>
          <p className="sub">
            The complete repository — godown records, every month's confirmations and issues,
            overrides, cut-offs, masters and user accounts — in one JSON file.
          </p>
        </div>
      </div>

      {notice && (
        <div className="upload-result">
          <button className="modal-x" onClick={() => setNotice(null)} aria-label="Dismiss">✕</button>
          <div className="upload-summary">✅ {notice}</div>
        </div>
      )}

      {/* ── Export ── */}
      <div className="card" style={{marginBottom: 16}}>
        <h3 style={{fontSize: 14, marginBottom: 6}}>Download a backup</h3>
        <p className="text-sm text-muted" style={{marginBottom: 12}}>
          Saves <strong>{backupFilename()}</strong> to your downloads folder. Covers all months
          in one file. Keep it somewhere off this server — a copy stored only on the server
          will not survive losing the server.
        </p>
        <button className="btn btn-p" onClick={handleExport} disabled={busy === 'export'}>
          {busy === 'export' ? 'Preparing…' : '📥 Export Complete Data'}
        </button>
        <p className="text-xs text-muted" style={{marginTop: 10}}>
          The file contains user accounts, including their encrypted passwords. Treat it as confidential.
        </p>
      </div>

      {/* ── Restore (admin only) ── */}
      {!isAdmin ? (
        <div className="card">
          <h3 style={{fontSize: 14, marginBottom: 6}}>Restore</h3>
          <p className="text-sm text-muted">Restoring a backup is restricted to the Admin account.</p>
        </div>
      ) : (
        <div className="card card-danger">
          <h3 style={{fontSize: 14, marginBottom: 6, color: 'var(--red)'}}>⚠️ Restore from a backup file</h3>
          <p className="text-sm" style={{marginBottom: 12}}>
            This <strong>replaces everything</strong>. All current godowns, confirmations, overrides,
            cut-offs, masters and users are cleared and rebuilt from the file, so anything recorded
            after the backup was taken is lost. It runs as a single transaction — if it fails at any
            point, nothing is changed.
          </p>

          <div className="field" style={{maxWidth: 420}}>
            <label>Backup file</label>
            <input type="file" accept=".json" onChange={handleFile} />
          </div>

          {parsed && (
            <>
              <div className="restore-preview">
                <div className="fw-600 text-sm" style={{marginBottom: 6}}>
                  {file?.name} — taken {parsed.meta?.generated_at
                    ? new Date(parsed.meta.generated_at).toLocaleString('en-IN')
                    : 'date unknown'}
                  {parsed.meta?.generated_by ? ` by ${parsed.meta.generated_by}` : ''}
                </div>
                <table className="diff-table">
                  <thead><tr><th>Contents</th><th>Rows to restore</th></tr></thead>
                  <tbody>
                    {counts.map(([t, n]) => (
                      <tr key={t}>
                        <td className="fw-600">{TABLE_LABEL[t] || t}</td>
                        <td className={n === 0 ? 'text-muted' : 'text-green fw-600'}>{n}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="form-grid" style={{maxWidth: 560, marginTop: 12}}>
                <div className="field">
                  <label>Your admin password *</label>
                  <input type="password" value={password} onChange={e => setPassword(e.target.value)} />
                </div>
                <div className="field">
                  <label>Type RESTORE to confirm *</label>
                  <input value={typed} onChange={e => setTyped(e.target.value)} placeholder="RESTORE" />
                </div>
              </div>

              <button className="btn btn-danger" onClick={handleRestore}
                disabled={busy === 'restore' || typed !== 'RESTORE' || !password}>
                {busy === 'restore' ? 'Restoring…' : '⚠️ Replace all data with this backup'}
              </button>
            </>
          )}
        </div>
      )}

      {errModal && (
        <ErrorModal title={errModal.title} messages={errModal.messages} onClose={() => setErrModal(null)} />
      )}
    </div>
  );
}
